<script setup lang="ts">
  import { ref, watch } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type {
    TGuestPreviewFocus,
    TReservationGuideScreen,
  } from '@/domain/reservations/reservation-guide';
  import type { IGuestPreviewModel } from '@/domain/reservations/reservation-guest-preview';
  import V2ReservationsGuestScreen from './V2ReservationsGuestScreen.vue';

  /**
   * Places the guest replica: a sticky aside beside the step on wide screens,
   * a collapsible block under it otherwise. The phone frame is rendered once;
   * the two layouts only toggle chrome around it, so a tier change never
   * re-mounts anything.
   */
  const props = defineProps<{
    model: IGuestPreviewModel;
    focus: TGuestPreviewFocus;
    layout: 'aside' | 'inline';
    caption: string;
    captionTone?: 'warning';
    /** One line under the inline header; the caption when omitted. */
    summary?: string;
    screen: TReservationGuideScreen;
    /** The inline block opens by default on the finish screen only. */
    defaultOpen: boolean;
    restaurantName: string;
    voseo: boolean;
  }>();
  const open = ref(props.defaultOpen);
  watch(
    () => props.screen,
    () => {
      open.value = props.defaultOpen;
    }
  );
</script>

<template>
  <aside
    class="reservations-guide-preview"
    :class="`layout-${layout}`"
    :aria-label="$t('reservations.guide.preview.title')"
  >
    <p v-show="layout === 'aside'" class="aside-title">
      {{ $t('reservations.guide.preview.title') }}
    </p>
    <button
      v-show="layout === 'inline'"
      type="button"
      class="inline-toggle"
      :aria-expanded="open ? 'true' : 'false'"
      aria-controls="reservations-guide-preview-body"
      @click="open = !open"
    >
      <span class="toggle-text">
        <span class="toggle-title">{{
          $t('reservations.guide.preview.title')
        }}</span>
        <span class="toggle-summary">{{ summary || caption }}</span>
      </span>
      <PosUiIcon :name="open ? 'chevron-up' : 'chevron-down'" :size="22" />
    </button>
    <div
      v-show="layout === 'aside' || open"
      id="reservations-guide-preview-body"
      class="preview-body"
    >
      <span class="preview-label">{{
        $t('reservations.guide.previewLabel')
      }}</span>
      <V2ReservationsGuestScreen
        :model="model"
        :focus="focus"
        :restaurant-name="restaurantName"
        :voseo="voseo"
      />
      <p
        class="preview-caption"
        :class="{ 'is-warning': captionTone === 'warning' }"
      >
        {{ caption }}
      </p>
    </div>
  </aside>
</template>

<style scoped lang="scss">
  .reservations-guide-preview {
    @apply tw-min-w-0 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    p {
      @apply tw-m-0;
    }
    .aside-title {
      @apply tw-px-4 tw-pt-4 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .inline-toggle {
      @apply tw-flex tw-min-h-14 tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border-0 tw-bg-transparent tw-px-4 tw-py-2 tw-text-left tw-text-neutral-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      .pos-ui-icon {
        @apply tw-shrink-0 tw-text-neutral-500;
      }
    }
    .toggle-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5;
    }
    .toggle-title {
      @apply tw-text-sm tw-font-semibold;
    }
    .toggle-summary {
      @apply tw-truncate tw-text-xs tw-text-neutral-500;
    }
    .preview-body {
      @apply tw-flex tw-flex-col tw-gap-3 tw-p-4;
    }
    .preview-label {
      @apply tw-text-center tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400;
    }
    .preview-caption {
      @apply tw-text-center tw-text-xs tw-leading-relaxed tw-text-neutral-500;
      &.is-warning {
        @apply tw-text-amber-700;
      }
    }
    // Aside: the frame follows the reader while the step scrolls.
    &.layout-aside {
      @apply tw-sticky tw-top-4 tw-self-start;
    }
  }
</style>
